import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { ProductVariation } from '../types';
import { useShopping } from '../contexts/ShoppingContext';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { useGuestMode } from '../hooks/useGuestMode';
import { Modal, Button, ImageGallery } from './ui';
import colorMap from '../data/colors.json';

function getTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export function QuickViewModal() {
  const { state, dispatch } = useShopping();
  const { clientData } = useClientDataContext();
  const { isGuest } = useGuestMode();
  const storeIsOpen = !isGuest || clientData?.store?.isOpen !== false;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);

  const { quickViewProduct, isQuickViewOpen } = state;

  // Get current images based on hovered or selected variation
  const getCurrentImages = () => {
    if (!quickViewProduct) return [];

    // If hovering over a color, show that variation's images
    if (hoveredColor) {
      const hoveredVariation = quickViewProduct.variations.find(v =>
        v.color === hoveredColor && (selectedVariation ? v.size === selectedVariation.size : true)
      );
      if (hoveredVariation?.images && hoveredVariation.images.length > 0) {
        return hoveredVariation.images;
      }
    }

    // Otherwise show selected variation images
    if (selectedVariation?.images && selectedVariation.images.length > 0) {
      return selectedVariation.images;
    }
    return quickViewProduct.images;
  };

  if (!isQuickViewOpen || !quickViewProduct) return null;

  const variation = selectedVariation || quickViewProduct.variations[0];
  const hasVariations = quickViewProduct.variations.length > 0;
  const minOrderQty = quickViewProduct.minOrderQty && quickViewProduct.minOrderQty > 1 ? quickViewProduct.minOrderQty : 1;

  // For products without variations, use product-level stock
  const effectiveStock = hasVariations ? (variation?.stock ?? 0) : (quickViewProduct.stock ?? 0);

  // Can order if: backorder/ondemand allowed OR stock >= minOrderQty
  const canOrder = quickViewProduct.allowBackorder || quickViewProduct.allowOnDemand || effectiveStock >= minOrderQty;

  const handleAddToCart = () => {
    if (hasVariations && variation) {
      dispatch({
        type: 'ADD_TO_CART',
        payload: { product: quickViewProduct, variation, quantity: minOrderQty }
      });
      dispatch({ type: 'CLOSE_QUICK_VIEW' });
    } else if (!hasVariations) {
      // For products without variations, create a default variation
      dispatch({
        type: 'ADD_TO_CART',
        payload: {
          product: quickViewProduct,
          variation: {
            id: quickViewProduct.id,
            size: 'Standard',
            color: 'Default',
            price: quickViewProduct.basePrice,
            stock: quickViewProduct.stock ?? 0
          },
          quantity: minOrderQty
        }
      });
      dispatch({ type: 'CLOSE_QUICK_VIEW' });
    }
  };

  const handleClose = () => {
    dispatch({ type: 'CLOSE_QUICK_VIEW' });
    setSelectedVariation(null);
  };

  const handleViewFullProduct = () => {
    dispatch({ type: 'CLOSE_QUICK_VIEW' });
    // Preserve department and category IDs from current URL
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    navigate(`/product/${quickViewProduct.id}${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <Modal
      isOpen={isQuickViewOpen}
      onClose={handleClose}
      size="md"
      closeOnBackdrop={true}
      closeOnEscape={true}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image - Smaller */}
        <div className="sm:w-1/2">
          <ImageGallery
            images={getCurrentImages()}
            alt={quickViewProduct.name}
            showThumbnails={false}
            enableKeyboardNavigation={false}
          />
        </div>

        {/* Product Details - Compact */}
        <div className="sm:w-1/2 p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {quickViewProduct.name}
          </h2>

          {/* Price */}
          <div className="mb-4">
            <span className="text-xl font-bold text-gray-900">
              ${variation?.price || quickViewProduct.basePrice}
            </span>
          </div>

          {/* Quick Variations - Horizontally Scrollable */}
          <div className="space-y-3 mb-4">
            {/* Color Selection - Scrollable */}
            {(() => {
              const colors = Array.from(new Set(quickViewProduct.variations.map(v => v.color))).filter(c => c && c !== 'Default');
              return colors.length > 0 ? (
                <div>
                  <h3 className="text-xs font-medium text-gray-900 mb-1">Color</h3>
                  <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
                    <div className="flex gap-1 min-w-min">
                      {colors.map(color => {
                        const bgColor = colorMap[color as keyof typeof colorMap] || '#E5E7EB';
                        const textColor = getTextColor(bgColor);
                        return (
                          <button
                            key={color}
                            onClick={() => {
                              const newVariation = quickViewProduct.variations.find(v => v.color === color);
                              setSelectedVariation(newVariation || null);
                            }}
                            onMouseEnter={() => setHoveredColor(color)}
                            onMouseLeave={() => setHoveredColor(null)}
                            style={{
                              backgroundColor: bgColor,
                              color: textColor,
                              borderColor: variation?.color === color ? '#2563EB' : '#D1D5DB'
                            }}
                            className={`px-2 py-1 text-xs border rounded font-medium transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              variation?.color === color ? 'ring-2 ring-blue-600' : 'hover:border-gray-400'
                            }`}
                            aria-label={`Select ${color} color`}
                            aria-pressed={variation?.color === color}
                          >
                            {color}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Size Selection - Scrollable */}
            {quickViewProduct.variations.some(v => v.size !== 'Standard') && (
              <div>
                <h3 className="text-xs font-medium text-gray-900 mb-1">Size</h3>
                <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
                  <div className="flex gap-1 min-w-min">
                    {Array.from(new Set(quickViewProduct.variations.map(v => v.size))).map(size => (
                      <button
                        key={size}
                        onClick={() => {
                          const newVariation = quickViewProduct.variations.find(v =>
                            v.size === size && (selectedVariation ? v.color === selectedVariation.color : true)
                          ) || quickViewProduct.variations.find(v => v.size === size);
                          setSelectedVariation(newVariation || null);
                        }}
                        className={`px-2 py-1 text-xs border rounded font-medium transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          variation?.size === size
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        aria-label={`Select size ${size}`}
                        aria-pressed={variation?.size === size}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Stock Info */}
          <p className="text-xs text-gray-500 mb-4">
            {effectiveStock > 0 ? `${effectiveStock} in stock` : quickViewProduct.allowOnDemand ? 'Available on demand' : 'Out of stock'}
          </p>

          {/* Action Buttons - Compact */}
          <div className="space-y-2">
            <Button
              onClick={handleAddToCart}
              disabled={!storeIsOpen || !canOrder}
              variant="primary"
              size="sm"
              fullWidth={true}
              icon={<ShoppingCart className="w-4 h-4" />}
            >
              {storeIsOpen ? 'Quick Add' : 'Store Closed'}
            </Button>
            
            <Button
              onClick={handleViewFullProduct}
              variant="outline"
              size="sm"
              fullWidth={true}
            >
              View Full Details
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}